import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './TableRowActionMenu.css';

const VIEWPORT_GAP = 4;
const VIEWPORT_PADDING = 8;

export default function TableRowActionMenu({
    isOpen,
    onOpenChange,
    children,
    openTriggerStyle,
}) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [position, setPosition] = useState(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;

        const triggerRect = trigger.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const placeAbove = window.innerHeight - triggerRect.bottom < menuRect.height + VIEWPORT_GAP
            && triggerRect.top >= menuRect.height + VIEWPORT_GAP;

        setPosition({
            placement: placeAbove ? 'above' : 'below',
            top: placeAbove
                ? triggerRect.top - menuRect.height - VIEWPORT_GAP
                : triggerRect.bottom + VIEWPORT_GAP,
            left: Math.max(
                VIEWPORT_PADDING,
                Math.min(
                    triggerRect.right - menuRect.width,
                    window.innerWidth - menuRect.width - VIEWPORT_PADDING,
                ),
            ),
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const closeOnOutsidePointerDown = (event) => {
            if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
                onOpenChange(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsidePointerDown);
        return () => document.removeEventListener('mousedown', closeOnOutsidePointerDown);
    }, [isOpen, onOpenChange]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                title="Actions"
                aria-label="Actions"
                className="bom-action-trigger"
                style={isOpen ? openTriggerStyle : undefined}
                onClick={() => onOpenChange(!isOpen)}
            >
                ⋮
            </button>
            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    role="menu"
                    className={`bom-action-menu table-row-action-menu table-row-action-menu--${position?.placement || 'below'}`}
                    style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {children}
                </div>,
                document.body,
            )}
        </>
    );
}
